'use client';

import { useToast } from '@common/hooks';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { AuthApi } from '@/api';
import { DONATION_ROUTES, ROUTES } from '@/constants';
import { useAuthStore } from '@/stores/AuthStore';

import { MarketButton } from './MarketButton';
import { MarketShareDrawer } from './MarketShareDrawer';
import { type ModalType, RandomTicketModal } from './RandomTicketModal';

interface RandomDrawClientProps {
  className?: string;
  children?: React.ReactNode;
  dimensions?: 'H56' | 'H48' | 'H40' | 'H32' | 'H26' | null | undefined;
}

interface ModalState {
  type: ModalType;
  point: number;
}

export const RandomDrawClient = ({
  className,
  children,
  dimensions,
}: RandomDrawClientProps) => {
  const router = useRouter();
  const { toastError } = useToast();
  const [modalState, setModalState] = useState<ModalState | null>(null);
  const [isShareDrawerOpen, setIsShareDrawerOpen] = useState(false);

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      const { data } =
        await AuthApi[
          'get:/wello/v2/auth/home/event/december/random-ticket-count'
        ]();

      const context = data?.context;
      if (!context) throw new Error('context is empty');

      const { randomTicketId: random_ticket_id, event2AvailYN: event2_avail_yn } = context;

      if (random_ticket_id === null) {
        return {
          type: (event2_avail_yn
            ? 'not-eligible'
            : 'already-participated') as ModalType,
          point: 0,
        };
      }

      const { data: openData } = await AuthApi[
        'put:/wello/v2/auth/home/event/december/random-ticket/{randomTicketId}'
      ]({ path: { randomTicketId: random_ticket_id } });

      const pointContext = openData?.context;
      if (!pointContext?.resultYN) {
        throw new Error(
          pointContext?.message || '웰포인트 지급에 실패했습니다.',
        );
      }

      return { type: 'win' as ModalType, point: pointContext.addPoints };
    },
    onSuccess: (result) => setModalState(result),
    onError: (error) => toastError({ error }),
  });

  const handleClick = () => {
    const { isLogin } = useAuthStore.getState();

    if (!isLogin) {
      router.push(
        ROUTES.LOGIN.withSearchParams({
          searchParams: { redirect: location.href },
        }),
      );

      return;
    }

    mutate();
  };

  const handlePrimaryClick = () => {
    if (!modalState) return;

    setModalState(null);

    switch (modalState.type) {
      case 'win':
        router.push(ROUTES.POINT_MARKET.pathname);
        break;
      case 'not-eligible':
        router.push(
          `${DONATION_ROUTES.HOME.pathname}?utm_source=wello&utm_medium=max_event&utm_campaign=buttn_donation&utm_content=donation_251203`,
        );
        break;
      case 'already-participated':
        setIsShareDrawerOpen(true);
        break;
    }
  };

  return (
    <>
      <MarketButton
        dimensions={dimensions}
        className={className}
        onClick={handleClick}
        disabled={isPending}
      >
        {children}
      </MarketButton>
      <RandomTicketModal
        isOpen={modalState !== null}
        onClose={() => setModalState(null)}
        type={modalState?.type ?? 'win'}
        point={modalState?.point ?? 0}
        onPrimaryClick={handlePrimaryClick}
      />
      <MarketShareDrawer
        opened={isShareDrawerOpen}
        onClose={() => setIsShareDrawerOpen(false)}
      />
    </>
  );
};
